/**
 * Main content analyzer for ludicrous-gateway
 * Orchestrates token estimation and keyword detection
 */

import type { MessagesRequest, Message } from "../types/messages.js";
import type { ContentAnalysis, ContentAnalyzer } from "../types/routing.js";
import {
  estimateRequestTokens,
  getRequestTextLength,
} from "./token-estimator.js";
import {
  KeywordDetector,
  createDefaultKeywordDetector,
} from "./keyword-detector.js";
import type { RoutingRuleConfig } from "../types/config.js";

/**
 * Content analyzer configuration
 */
export interface ContentAnalyzerConfig {
  /** Custom keywords from routing rules */
  ruleKeywords?: string[];
  /** Custom patterns from routing rules */
  rulePatterns?: string[];
  /** Additional sensitive keywords */
  additionalKeywords?: string[];
}

/**
 * Default content analyzer implementation
 */
export class DefaultContentAnalyzer implements ContentAnalyzer {
  private keywordDetector: KeywordDetector;

  constructor(config: ContentAnalyzerConfig = {}) {
    // Start with default sensitive keywords
    this.keywordDetector = createDefaultKeywordDetector();

    // Add keywords from routing rules
    if (config.ruleKeywords?.length) {
      this.keywordDetector.addKeywords(config.ruleKeywords);
    }

    // Add patterns from routing rules
    if (config.rulePatterns?.length) {
      this.keywordDetector.addPatterns(config.rulePatterns);
    }

    // Add any additional keywords
    if (config.additionalKeywords?.length) {
      this.keywordDetector.addKeywords(config.additionalKeywords);
    }
  }

  /**
   * Analyze a request for routing decisions
   */
  analyze(request: MessagesRequest): ContentAnalysis {
    // Estimate token count
    const tokenEstimate = estimateRequestTokens(request);

    // Get content length
    const contentLength = getRequestTextLength(request);

    // Detect keywords and patterns
    const keywordResult = this.keywordDetector.detectInRequest(request);

    // Detect tool usage
    const hasTools = Array.isArray(request.tools) && request.tools.length > 0;
    const hasToolUseBlocks = this.detectToolUseBlocks(request.messages);

    return {
      estimatedTokens: tokenEstimate.total,
      matchedKeywords: keywordResult.matchedKeywords,
      matchedPatterns: keywordResult.matchedPatterns,
      requestedModel: request.model,
      hasSensitiveContent: keywordResult.isSensitive,
      contentLength,
      hasTools,
      hasToolUseBlocks,
    };
  }

  /**
   * Detect if messages contain tool_use or tool_result blocks
   */
  private detectToolUseBlocks(messages: Message[]): boolean {
    for (const msg of messages) {
      if (Array.isArray(msg.content)) {
        for (const block of msg.content) {
          if (block.type === "tool_use" || block.type === "tool_result") {
            return true;
          }
        }
      }
    }
    return false;
  }

  /**
   * Add keywords from routing rules
   */
  addRuleKeywords(keywords: string[]): void {
    this.keywordDetector.addKeywords(keywords);
  }

  /**
   * Add patterns from routing rules
   */
  addRulePatterns(patterns: string[]): void {
    this.keywordDetector.addPatterns(patterns);
  }
}

/**
 * Extract all keywords and patterns from routing rules
 */
export function extractRuleKeywordsAndPatterns(rules: RoutingRuleConfig[]): {
  keywords: string[];
  patterns: string[];
} {
  const keywords: string[] = [];
  const patterns: string[] = [];

  for (const rule of rules) {
    if (rule.conditions.keywords) {
      keywords.push(...rule.conditions.keywords);
    }
    if (rule.conditions.patterns) {
      patterns.push(...rule.conditions.patterns);
    }
  }

  return {
    keywords: [...new Set(keywords)], // Deduplicate
    patterns: [...new Set(patterns)],
  };
}

/**
 * Create a content analyzer from routing rules
 */
export function createAnalyzerFromRules(
  rules: RoutingRuleConfig[]
): DefaultContentAnalyzer {
  const { keywords, patterns } = extractRuleKeywordsAndPatterns(rules);

  return new DefaultContentAnalyzer({
    ruleKeywords: keywords,
    rulePatterns: patterns,
  });
}

// Export token estimator functions for direct use
export {
  estimateTokensFromText,
  estimateTokensFromMessage,
  estimateRequestTokens,
  getRequestTextLength,
} from "./token-estimator.js";

// Export keyword detector for customization
export {
  KeywordDetector,
  createDefaultKeywordDetector,
  DEFAULT_SENSITIVE_KEYWORDS,
} from "./keyword-detector.js";
